import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, GraduationCap, CalendarClock, FileText, DollarSign, MessageSquare, Sparkles, ShoppingBag, HelpCircle, BarChart2, UserCircle, UserCog, CreditCard, CircleDot, Calendar, Bell, Repeat, MonitorSpeaker, Video, X, Brain, ImageIcon } from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { useAuth } from "@/hooks/useAuth";
import { useResponsive } from "@/hooks/useResponsive";
import { useNavigationSettings } from "@/hooks/useNavigationSettings";
import { useHivePackTerminology } from "@/hooks/useHivePackCustomization";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";
import { SidebarFooter } from "./SidebarFooter";
import { hiveShareNavItems } from "@/config/navigationConfig";
interface AdminSidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
}
interface SidebarItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  navigationKey?: keyof import("@/types/navigationTypes").NavigationSettings;
}
export default function AdminSidebar({
  isOpen = true,
  onToggle,
  onClose
}: AdminSidebarProps) {
  const {
    signOut,
    userRole,
    organizationId
  } = useAuth();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [isHovered, setIsHovered] = useState(false);
  const { navigationSettings } = useNavigationSettings();
  const terminology = useHivePackTerminology(organizationId);
  const { isHiveShareOnly, loading: subscriptionLoading } = useSubscriptionAccess();

  // Keyboard support for closing sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && (isMobile || isTablet)) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile, isTablet, onClose]);
  const renderLogo = () => (
    <div className="flex items-center justify-between py-6 px-4 border-b border-gray-200/60 bg-white/80 backdrop-blur-sm">
      <img 
        src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
        alt="Company Logo" 
        className={cn(
          "object-contain",
          isMobile ? "h-8 w-auto" : "h-10 w-auto"
        )}
        onError={(e) => {
          // Fallback to text if image fails to load
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          target.parentElement!.innerHTML = '<span class="text-gray-600 font-bold text-2xl">H</span>';
        }}
      />
      
      {(isMobile || isTablet) && (
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose || onToggle}
          className="rounded-xl p-2"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close Menu</span>
        </Button>
      )}
    </div>
  );
  const managementItems: SidebarItem[] = [{
    title: "Dashboard",
    href: "/dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />
  }, {
    title: "Families",
    href: "/admin/families",
    icon: <Users className="h-4 w-4" />
  }, {
    title: terminology.students.charAt(0).toUpperCase() + terminology.students.slice(1),
    href: "/admin/students",
    icon: <GraduationCap className="h-4 w-4" />
  }, {
    title: terminology.instructors.charAt(0).toUpperCase() + terminology.instructors.slice(1),
    href: "/admin/staff",
    icon: <UserCog className="h-4 w-4" />
  }, {
    title: terminology.classes.charAt(0).toUpperCase() + terminology.classes.slice(1),
    href: "/admin/classes",
    icon: <CircleDot className="h-4 w-4" />
  }, {
    title: `Makeup ${terminology.classes.charAt(0).toUpperCase() + terminology.classes.slice(1)}`,
    href: "/admin/makeup-classes",
    icon: <Repeat className="h-4 w-4" />,
    navigationKey: "makeup_classes"
  }, {
    title: "Seasons",
    href: "/admin/seasons",
    icon: <Calendar className="h-4 w-4" />,
    navigationKey: "seasons"
  }, {
    title: "Schedule",
    href: "/admin/schedule",
    icon: <CalendarClock className="h-4 w-4" />
  }, {
    title: "Check-in Kiosk",
    href: "/check-in",
    icon: <MonitorSpeaker className="h-4 w-4" />,
    navigationKey: "check_in_kiosk"
  }];
  const financialItems: SidebarItem[] = [{
    title: "Bill Assist",
    href: "/admin/bill-assist",
    icon: <Brain className="h-4 w-4" />,
    navigationKey: "bill_assist"
  }, {
    title: "Invoices",
    href: "/admin/invoices",
    icon: <FileText className="h-4 w-4" />,
    navigationKey: "invoices"
  }, {
    title: "Auto-Billing",
    href: "/admin/billing-schedules",
    icon: <CreditCard className="h-4 w-4" />,
    navigationKey: "auto_billing"
  }, {
    title: "Payments",
    href: "/admin/payments-dashboard",
    icon: <DollarSign className="h-4 w-4" />,
    navigationKey: "payments"
  }];
  const communicationItems: SidebarItem[] = [{
    title: "Messages",
    href: "/admin/messages",
    icon: <MessageSquare className="h-4 w-4" />,
    navigationKey: "messages"
  }, {
    title: "Notifications",
    href: "/admin/notifications",
    icon: <Bell className="h-4 w-4" />,
    navigationKey: "notifications"
  }, {
    title: "Parent Portal",
    href: "/admin/parent-portal-settings",
    icon: <UserCircle className="h-4 w-4" />,
    navigationKey: "parent_portal"
  }, {
    title: "Documents",
    href: "/admin/document-management",
    icon: <FileText className="h-4 w-4" />,
    navigationKey: "document_management"
  }];
  const dataItems: SidebarItem[] = [{
    title: "Reporting",
    href: "/admin/reporting",
    icon: <BarChart2 className="h-4 w-4" />,
    navigationKey: "reporting"
  }, {
    title: "AI Content",
    href: "/admin/ai-content-generator",
    icon: <Sparkles className="h-4 w-4" />,
    navigationKey: "ai_content"
  }, {
    title: "HiveShare",
    href: "/admin/hiveshare",
    icon: <Video className="h-4 w-4" />,
    navigationKey: "hiveshare"
  }];
  const otherItems: SidebarItem[] = [{
    title: "Apparel",
    href: "/admin/apparel",
    icon: <ShoppingBag className="h-4 w-4" />,
    navigationKey: "apparel"
  }, {
    title: "Subscription",
    href: "/admin/subscription-center",
    icon: <CreditCard className="h-4 w-4" />
  }, {
    title: "Data Import",
    href: "/admin/data-import",
    icon: <FileText className="h-4 w-4" />,
    navigationKey: "data_import"
  }, {
    title: "Help & Support",
    href: "/admin/help",
    icon: <HelpCircle className="h-4 w-4" />
  }];

  // Admin-only items
  const adminItems = userRole === 'superadmin' ? [{
    title: "Stripe Connect Requests",
    href: "/admin/stripe-connect-requests",
    icon: <CreditCard className="h-4 w-4" />
  }, {
    title: "Support Chat",
    href: "/superadmin/admin-chat",
    icon: <MessageSquare className="h-4 w-4" />
  }] : [];
  const renderSidebarGroup = (items: SidebarItem[], title?: string) => {
    // Filter items based on navigation settings
    const visibleItems = items.filter(item => {
      // If item has no navigationKey, always show it (core features)
      if (!item.navigationKey) return true;
      // Otherwise, check if the feature is enabled
      return navigationSettings[item.navigationKey] !== false;
    });

    // Don't render empty groups
    if (visibleItems.length === 0) return null;

    return (
      <div className="py-[4px] px-[10px] mx-px">
        {title && <h3 className="mb-2 px-4 text-left text-sm font-bold text-indigo-500">{title}</h3>}
        {visibleItems.map((item, index) => {
          if (item.title === "Check-in Kiosk") {
            // Open in new tab, styled like NavLink
            return (
              <a
                key={index}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn("h-10 flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 bg-background text-foreground hover:bg-accent hover:text-accent-foreground")}
                style={{ textDecoration: "none" }}
              >
                {item.icon}
                <span className="ml-2 transition-opacity duration-200">{item.title}</span>
              </a>
            );
          }
          return (
            <NavLink 
              key={index} 
              to={item.href} 
              className={({ isActive }) => cn(
                isActive ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-background text-foreground hover:bg-accent hover:text-accent-foreground", 
                "h-10 flex items-center justify-start px-4 py-2 rounded-md text-sm font-medium transition-all duration-200"
              )} 
              onClick={() => (isMobile || isTablet) && (onClose?.() || onToggle?.())}
            >
              {item.icon}
              <span className="ml-2 transition-opacity duration-200">{item.title}</span>
            </NavLink>
          );
        })}
      </div>
    );
  };
  // Responsive sidebar styling
  const sidebarClasses = cn(
    "h-screen bg-white flex flex-col overflow-hidden relative",
    // Mobile: Full screen overlay
    isMobile && [
      "fixed inset-0 z-50 shadow-2xl",
      "w-screen"
    ],
    // Tablet: Slide-out panel
    isTablet && [
      "fixed left-0 top-0 z-40 shadow-2xl border-r border-gray-200/50",
      "w-72"
    ],
    // Desktop: Fixed sidebar
    isDesktop && [
      "w-52 shadow-lg border-r border-gray-200/60",
      "bg-white/95 backdrop-blur-sm"
    ]
  );

  const sidebarContent = (
    <motion.div
      className={sidebarClasses}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
      initial={isMobile || isTablet ? { x: -100, opacity: 0 } : {}}
      animate={isMobile || isTablet ? { x: 0, opacity: 1 } : {}}
      exit={isMobile || isTablet ? { x: -100, opacity: 0 } : {}}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Background gradient */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{ 
          background: isHovered && !isMobile
            ? "radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)"
            : "radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%)"
        }}
        transition={{ duration: 0.5 }}
      />

      <div className="relative z-10 flex flex-col h-full">
        {renderLogo()}
        
        <ScrollArea className="flex-1 overflow-hidden">
          <div className="pb-12 pt-4">
            {subscriptionLoading ? (
              // Show nothing while loading to prevent flash
              null
            ) : isHiveShareOnly ? (
              <>
                {/* Simplified navigation for HiveShare-only users */}
                <div className="px-3 pb-4">
                  <div className="space-y-1">
                    {hiveShareNavItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                            isActive
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          )
                        }
                      >
                        <item.icon className="mr-3 h-5 w-5" />
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
                {/* Help & Support for HiveShare users */}
                <div className="px-3 pt-4 border-t">
                  <NavLink
                    to="/help"
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                        isActive
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )
                    }
                  >
                    <HelpCircle className="mr-3 h-5 w-5" />
                    Help & Support
                  </NavLink>
                </div>
              </>
            ) : (
              <>
                {/* Full navigation for regular Hivetivity users - nothing changes for them */}
                {renderSidebarGroup(managementItems, "Admin Management")}
                {renderSidebarGroup(financialItems, "Financial")}
                {renderSidebarGroup(communicationItems, "Communication")}
                {renderSidebarGroup(dataItems, "Data & Content")}
                {renderSidebarGroup(otherItems, "Other")}
                {adminItems.length > 0 && renderSidebarGroup(adminItems, "Super Admin")}
              </>
            )}
          </div>
        </ScrollArea>
        
        <SidebarFooter />
      </div>
    </motion.div>
  );

  // Mobile/Tablet: Render with backdrop
  if (isMobile || isTablet) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose || onToggle}
            />
            {sidebarContent}
          </>
        )}
      </AnimatePresence>
    );
  }

  // Desktop: Always visible
  return sidebarContent;
}
